// Fonti ufficiali coperte da Trovanorma. Allineate al registry delle fonti
// del backend: aggiornare qui quando viene aggiunto un cluster di fonti.
// Solo fonti realmente coperte: mai aggiungere enti non presenti nel registry.
// Usate da: SourcesStrip (homepage), SourcesOverview (indice funzionalità),
// pagina /fonti.

import type { PublicSource } from '@/api/sources/types';

export type SourceCategoryId = 'normativa' | 'prassi' | 'giurisprudenza' | 'contratti-collettivi';

export interface SourceCategory {
  id: SourceCategoryId;
  label: string;
  /** Voci puntate per la sezione panoramica (indice funzionalità) */
  items: string[];
}

export const sourceCategories: SourceCategory[] = [
  {
    id: 'normativa',
    label: 'Normativa',
    items: [
      'Leggi, decreti-legge, decreti legislativi, DPR e DPCM',
      'Regolamenti e direttive UE',
      'Decreti ministeriali, interministeriali e dirigenziali del MIT',
      'Legislazione e regolamentazione di Regioni',
    ],
  },
  {
    id: 'prassi',
    label: 'Prassi amministrativa',
    items: [
      'Interpelli, circolari, risoluzioni e provvedimenti dell’Agenzia delle Entrate',
      'Messaggi e circolari INPS',
      'Circolari e istruzioni operative INAIL',
      'Determinazioni, pareri e linee guida di ADM, Garante Privacy, AGCM, AGID e MIT',
    ],
  },
  {
    id: 'giurisprudenza',
    label: 'Giurisprudenza',
    items: [
      'Sentenze della Corte di Cassazione civile, penale e Sezione Lavoro',
      'Giurisprudenza tributaria di Cassazione, Corte Costituzionale, Corte di Giustizia UE e CTR',
      'Sentenze della Corte di Giustizia dell’Unione Europea',
      'Sentenze, ordinanze e pareri di Consiglio di Stato, CGARS e TAR, con Adunanza plenaria e Adunanza generale',
    ],
  },
  {
    id: 'contratti-collettivi',
    label: 'Contratti collettivi',
    items: [
      'Testi dei Contratti Collettivi Nazionali di Lavoro (CCNL)',
      'Accordi modificativi dei contratti',
    ],
  },
];

/**
 * Nomi mostrati nel nastro sotto l'hero, per istituzione invece che per
 * cluster. Passano dagli id del catalogo perché in home non si prometta una
 * fonte che non abbiamo: è già successo con «Corte Costituzionale» e «Codice
 * degli appalti», che non sono fonti ma un giudice citato in una descrizione e
 * una legge dentro la normativa italiana.
 */
const MARQUEE_LABELS: [id: string, label: string][] = [
  ['normattiva', 'Gazzetta Ufficiale'],
  ['italgiure_cassazione_civile', 'Corte di Cassazione'],
  ['ade', 'Agenzia delle Entrate'],
  ['inps', 'INPS'],
  ['inail', 'INAIL'],
  ['garante_privacy', 'Garante Privacy'],
  ['eurlex_giurisprudenza', 'Corte di Giustizia UE'],
  ['giustizia_amministrativa', 'Consiglio di Stato e TAR'],
  ['ccnl', 'CCNL'],
  ['adm', 'Agenzia delle Dogane'],
  ['regioni', 'Normativa regionale'],
];

export interface OfficialSource {
  /** Id del cluster nel backend: allinea questa voce a `/v1/landing/sources` */
  id: string;
  name: string;
  /** Sigla mostrata nel riquadro della fonte */
  initials: string;
  description: string;
  /** La prima categoria determina il gruppo nella pagina /fonti */
  categories: SourceCategoryId[];
  /**
   * Numero di fonti distinte raggruppate sotto questa voce (es. le 20
   * normative regionali). Vale 1 se assente; usato per il conteggio totale.
   */
  subsourcesCount?: number;
  /** Futuro upgrade "1b": logo in grigio al posto della pill, quando disponibile */
  logo?: { src: string; width: number; height: number };
}

export const officialSources: OfficialSource[] = [
  {
    id: 'normattiva',
    name: 'Normativa italiana',
    initials: 'N',
    description:
      'Leggi, decreti-legge, decreti legislativi, DPR e DPCM pubblicati nella Gazzetta Ufficiale.',
    categories: ['normativa'],
  },
  {
    id: 'ade',
    name: 'Agenzia delle Entrate',
    initials: 'AdE',
    description:
      'Interpelli, circolari, risoluzioni, provvedimenti e modelli ufficiali dell’Agenzia delle Entrate.',
    categories: ['prassi'],
  },
  {
    id: 'inail',
    name: 'INAIL',
    initials: 'INA',
    description:
      'Circolari e istruzioni operative dell’Istituto nazionale per l’assicurazione contro gli infortuni sul lavoro.',
    categories: ['prassi'],
  },
  {
    id: 'inps',
    name: 'INPS',
    initials: 'INP',
    description:
      'Messaggi, circolari e provvedimenti dell’Istituto nazionale della previdenza sociale.',
    categories: ['prassi'],
  },
  {
    id: 'italgiure_cassazione_civile',
    name: 'Giurisprudenza civile',
    initials: 'GC',
    description: 'Sentenze civili della Corte di Cassazione.',
    categories: ['giurisprudenza'],
  },
  {
    id: 'italgiure_cassazione_penale',
    name: 'Giurisprudenza penale',
    initials: 'GP',
    description: 'Sentenze penali della Corte di Cassazione.',
    categories: ['giurisprudenza'],
  },
  {
    id: 'italgiure_cassazione_lavoro',
    name: 'Giurisprudenza giuslavoristica',
    initials: 'GL',
    description: 'Sentenze della Sezione Lavoro della Corte di Cassazione.',
    categories: ['giurisprudenza'],
  },
  {
    id: 'finanze_giurisprudenza',
    name: 'Giurisprudenza tributaria',
    initials: 'GT',
    description:
      'Sentenze in materia tributaria di Corte di Cassazione, Corte Costituzionale, Corte di giustizia dell’Unione europea e corti di giustizia tributaria (già CTR).',
    categories: ['giurisprudenza'],
  },
  {
    id: 'giustizia_amministrativa',
    name: 'Giustizia amministrativa',
    initials: 'GA',
    description: 'Sentenze, ordinanze e pareri del Consiglio di Stato e dei TAR, comprese Adunanza plenaria, Adunanza generale e CGARS.',
    categories: ['giurisprudenza'],
  },
  {
    id: 'ccnl',
    name: 'CCNL',
    initials: 'CN',
    description:
      'Testi dei contratti collettivi nazionali di lavoro, inclusi gli accordi modificativi.',
    categories: ['contratti-collettivi'],
  },
  {
    id: 'garante_privacy',
    name: 'Garante Privacy',
    initials: 'GPDP',
    description:
      'Provvedimenti, pareri, linee guida e autorizzazioni del Garante per la protezione dei dati personali.',
    categories: ['prassi'],
  },
  {
    id: 'adm',
    name: 'Agenzia delle Dogane e dei Monopoli',
    initials: 'ADM',
    description: 'Circolari, determinazioni e provvedimenti.',
    categories: ['prassi'],
  },
  {
    // Cluster backend "Regioni": un gruppo, 20 fonti regionali distinte
    id: 'regioni',
    name: 'Normativa regionale',
    initials: 'REG',
    description:
      'Leggi e regolamenti delle Regioni italiane pubblicati nei Bollettini Ufficiali regionali.',
    categories: ['normativa'],
    subsourcesCount: 20,
  },
  {
    id: 'eurlex_normativa',
    name: 'Normativa europea',
    initials: 'UE',
    description: 'Regolamenti e direttive dell’Unione europea.',
    categories: ['normativa'],
  },
  {
    id: 'eurlex_giurisprudenza',
    name: 'Giurisprudenza europea',
    initials: 'GUE',
    description: 'Sentenze della Corte di giustizia dell’Unione europea.',
    categories: ['giurisprudenza'],
  },
  {
    id: 'mit',
    name: 'Ministero delle Infrastrutture e dei Trasporti',
    initials: 'MIT',
    description: 'Accordi, circolari e decreti (ministeriali, interministeriali e dirigenziali).',
    categories: ['normativa', 'prassi'],
  },
  {
    id: 'agcm',
    name: 'AGCM',
    initials: 'AGCM',
    description:
      'Provvedimenti, segnalazioni e pareri dell’Autorità Garante della Concorrenza e del Mercato in materia di concentrazioni, intese, abusi di posizione dominante, pratiche commerciali scorrette, pubblicità e clausole vessatorie.',
    categories: ['prassi'],
  },
  {
    id: 'agid',
    name: 'AgID',
    initials: 'AGID',
    description: 'Circolari, determinazioni e linee guida dell’Agenzia per l’Italia digitale.',
    categories: ['prassi'],
  },
];

/** Totale fonti distinte coperte (i gruppi contano per le loro sotto-fonti) */
export const marqueeSources: string[] = MARQUEE_LABELS.map(([id, label]) => {
  if (!officialSources.some(source => source.id === id))
    throw new Error(`Fonte del nastro assente dal catalogo: ${id}`);
  return label;
});

export const officialSourcesCount = officialSources.reduce(
  (total, source) => total + (source.subsourcesCount ?? 1),
  0
);

/**
 * Le etichette dei gruppi coincidono con i `tags` di `/v1/landing/sources`:
 * è quello che permette ai filtri di funzionare anche prima della risposta.
 */
const categoryLabel = (id: SourceCategoryId) =>
  sourceCategories.find(category => category.id === id)?.label ?? id;

/**
 * Catalogo statico nella forma della risposta live. È quanto finisce nell'HTML
 * esportato — quindi quello che leggono i motori di ricerca e chi ha JavaScript
 * disattivato. Appena arriva la risposta del backend le righe si aggiornano al
 * loro posto: stessi `id`, nessun rimontaggio e nessun salto di layout.
 */
/**
 * Documenti indicizzati, arrotondato per difetto: in pagina si scrive «1,3 milioni»
 * e il contatore sale fin qui prima di assestarsi sulla dicitura.
 */
export const indexedDocumentsCount = 1_300_000;

export const fallbackPublicSources: PublicSource[] = officialSources.map(source => ({
  id: source.id,
  name: source.name,
  description: source.description,
  initials: source.initials,
  tags: source.categories.map(categoryLabel),
  last_reference_document: null,
}));

function byName(name: string): OfficialSource {
  const source = officialSources.find(s => s.name === name);
  if (!source) throw new Error(`Fonte non trovata: ${name}`);
  return source;
}

/**
 * Due file del marquee della homepage, bilanciate a occhio per larghezza
 * complessiva (la seconda scorre in direzione opposta).
 */
export const officialSourcesRows: [OfficialSource[], OfficialSource[]] = [
  [
    'Normativa italiana',
    'Agenzia delle Entrate',
    'Giurisprudenza civile',
    'Giurisprudenza penale',
    'CCNL',
    'Garante Privacy',
    'Normativa regionale',
    'Normativa europea',
    'INPS',
  ].map(byName),
  [
    'INAIL',
    'Giurisprudenza giuslavoristica',
    'Giurisprudenza tributaria',
    'Agenzia delle Dogane e dei Monopoli',
    'Giurisprudenza europea',
    'Ministero delle Infrastrutture e dei Trasporti',
    'AGCM',
    'AgID',
    'Giustizia amministrativa',
  ].map(byName),
];
